import { useEffect, useState } from "react";
import api from "../utils/api";
import { NavStockYard } from "../constants/navigation";

/**
 * Fetches the stock yards the current user may see, for the STOCK navigation nodes.
 *
 * `GET /api/stock/yards` is filtered server-side by `visibleYardIds()`, so the response
 * is already the caller's access list — neither consumer re-derives it. Shared by the
 * desktop sidebar tree and the m.idarah.io landing grid so the two can never drift.
 *
 * `enabled` is the caller's `read:stock` check: without it there is nothing to ask for,
 * and we must not fire the request for users who cannot see the module.
 */
export const useStockYards = (enabled: boolean): NavStockYard[] => {
  const [yards, setYards] = useState<NavStockYard[]>([]);

  useEffect(() => {
    if (!enabled) {
      setYards([]);
      return;
    }
    const fetchYards = async () => {
      try {
        setYards(await api.get<NavStockYard[]>('/api/stock/yards', { skipErrorToast: true }) || []);
      } catch (error) {
        console.error('Error fetching stock yards for navigation:', error);
      }
    };
    fetchYards();

    // Yards are created, closed and re-membered from Settings; the event keeps both
    // consumers current without a refetch on every route change.
    window.addEventListener('stock-yards-changed', fetchYards);
    return () => window.removeEventListener('stock-yards-changed', fetchYards);
  }, [enabled]);

  return yards;
};
